/**
 * engines/cosmichistory
 *
 * カール・セーガンの「宇宙暦(Cosmic Calendar)」— 宇宙の138億年の歴史を
 * 1年間(1月1日 0:00 = ビッグバン、12月31日 24:00 = 現在)に圧縮して表現する
 * 手法を実装する。
 *
 * 【この engine の性質】
 * 宇宙暦そのものは「単位換算」であり、天体物理の法則ではない。しかし、
 * 換算に使う実際の年代(太陽系形成4.6億年前、ホモ・サピエンス誕生30万年前等)
 * は科学的に推定されている実測値であり、換算結果は広く引用されている
 * 通俗科学の定説(例:「人類の文明は大晦日の最後の10数秒」)と照合できる。
 * したがってこの engine は「検証可能」な部類に入る。
 */

export const UNIVERSE_AGE_YEARS = 13.8e9;
const DAYS_PER_YEAR = 365;
const SECONDS_PER_DAY = 24 * 3600;
const SECONDS_PER_COSMIC_YEAR = DAYS_PER_YEAR * SECONDS_PER_DAY;

export interface CosmicCalendarDate {
  /** 1月1日を0とした年間の通日 (0-based, 0=1/1) */
  dayOfYear: number;
  /** 月 (1-12) */
  month: number;
  /** 日 (1-31) */
  day: number;
  /** 時 (0-23) */
  hour: number;
  /** 分 (0-59) */
  minute: number;
  /** 秒 (0-59) */
  second: number;
  /** 現在(大晦日24:00)までの残り秒数 */
  secondsUntilNow: number;
  /** 年始からの経過秒数 */
  secondsFromStart: number;
}

const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * 「〜年前」の出来事を、宇宙暦上の日付・時刻に変換する。
 *
 * @param yearsAgo         現在からの経過年数(実年)
 * @param universeAgeYears 宇宙の年齢(実年, 既定138億年)
 */
export function cosmicCalendarDate(yearsAgo: number, universeAgeYears: number = UNIVERSE_AGE_YEARS): CosmicCalendarDate {
  if (yearsAgo < 0) throw new Error('yearsAgo must be non-negative');
  if (yearsAgo > universeAgeYears) throw new Error('yearsAgo cannot exceed the age of the universe');

  const ageSinceBigBangYears = universeAgeYears - yearsAgo;
  const fraction = ageSinceBigBangYears / universeAgeYears;
  const secondsFromStart = fraction * SECONDS_PER_COSMIC_YEAR;
  const secondsUntilNow = SECONDS_PER_COSMIC_YEAR - secondsFromStart;

  let remainingDays = Math.floor(secondsFromStart / SECONDS_PER_DAY);
  const secondsIntoDay = secondsFromStart - remainingDays * SECONDS_PER_DAY;
  // 端数で366日目に入ってしまう場合は365日目(12/31)に丸める
  if (remainingDays >= DAYS_PER_YEAR) remainingDays = DAYS_PER_YEAR - 1;
  const dayOfYear = remainingDays;

  let month = 1;
  let dayCursor = dayOfYear;
  for (const len of MONTH_LENGTHS) {
    if (dayCursor < len) break;
    dayCursor -= len;
    month += 1;
  }
  const day = dayCursor + 1;

  const hour = Math.floor(secondsIntoDay / 3600);
  const minute = Math.floor((secondsIntoDay % 3600) / 60);
  const second = Math.floor(secondsIntoDay % 60);

  return { dayOfYear, month, day, hour, minute, second, secondsUntilNow, secondsFromStart };
}

/** 日付を "M月D日 HH:MM:SS" の形式にフォーマットする(日本語表記)。 */
export function formatCosmicDateJa(date: CosmicCalendarDate): string {
  const hh = String(date.hour).padStart(2, '0');
  const mm = String(date.minute).padStart(2, '0');
  const ss = String(date.second).padStart(2, '0');
  return `${date.month}月${date.day}日 ${hh}:${mm}:${ss}`;
}

/** 日付を "Mon D, HH:MM:SS" の形式にフォーマットする(英語表記)。 */
export function formatCosmicDateEn(date: CosmicCalendarDate): string {
  const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hh = String(date.hour).padStart(2, '0');
  const mm = String(date.minute).padStart(2, '0');
  const ss = String(date.second).padStart(2, '0');
  return `${MONTHS_EN[date.month - 1]} ${date.day}, ${hh}:${mm}:${ss}`;
}

// ============================================================
// 早期宇宙タイムライン用（対数スケール位置計算）
// ============================================================

/**
 * ビッグバン後 t 秒の出来事を、対数スケールの正規化位置 [0,1] に変換する。
 * x=0 がビッグバン(最小の対数範囲)、x=1 が最大の対数範囲(既定は現在=138億年)。
 *
 * 早期宇宙(プランク時代〜再結合期)は10^-43秒から10^13秒程度まで、
 * 44桁にわたる範囲を扱うため対数スケール以外では表示できない。
 */
export function logTimePosition(
  secondsAfterBigBang: number,
  minExpSeconds: number = -43,
  maxExpSeconds: number = Math.log10(UNIVERSE_AGE_YEARS * 365.25 * 24 * 3600),
): number {
  if (secondsAfterBigBang <= 0) return 0;
  const logT = Math.log10(secondsAfterBigBang);
  const x = (logT - minExpSeconds) / (maxExpSeconds - minExpSeconds);
  return Math.max(0, Math.min(1, x));
}
